<template>
    <div>
        <div :class="[...alertContainerClasses, alertClass]" :role="withAlertRole? 'alert' : null">
            <p class="alert" :class="(!boldTitle ? 'ob1-alert-normal' : '') + (noBackground ? ' ob1-alert-nobackground' : '')">
                <span :class="['alert-icon', alertIconClass]" aria-hidden="true" />
                <span
                    :data-cs-mask="anonymized ? '' : null"
                    class="ob1-alert-title"
                    :class="!boldTitle ? 'font-weight-normal' : ''"
                    :data-test-id="dataTestId"
                    v-html="title"
                />
            </p>
            <div v-if="text" class="alert-content">
                <p
                    v-if="!collapseTitle"
                    class="alert-text"
                    v-html="text"
                />
                <button
                    v-if="collapseTitle"
                    :id="`${collapseId}-header`"
                    type="button"
                    :class="['ob1-link-arrow', 'ob1-link-dark', 'collapsed', buttonClass]"
                    data-bs-toggle="collapse"
                    :data-bs-target="`#${collapseId}-text`"
                    aria-expanded="false"
                    :aria-controls="`${collapseId}-text`"
                >
                    {{ collapseTitle }}
                </button>
                <div
                    v-if="collapseTitle"
                    :id="`${collapseId}-text`"
                    class="collapse"
                    :aria-labelledby="`${collapseId}-header`"
                    v-html="text"
                />
            </div>
            <div v-if="items" class="alert-content">
                <BulletList
                    :no-style="false"
                    :texts="items"
                />
            </div>
            <div v-if="withButton" class="alert-content">
                <a
                    :href="urlButton"
                    class="btn btn-secondary"
                    v-html="labelButton"
                />
            </div>
            <div v-if="$slots.content" class="alert-content">
                <slot name="content" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import BulletList from "./BulletList.vue";

type AlertType = "info" | "danger" | "success" | "warning" | "rewards" | "voucher" | undefined;

export default defineComponent({
    name: "AlertMessage",
    components: {
        BulletList
    },
    props: {
        title: {
            type: String,
            required: true
        },
        boldTitle: {
            type: Boolean,
            default: true
        },
        collapseTitle: {
            type: String,
            default: undefined
        },
        collapseId: {
            type: String,
            default: undefined
        },
        text: {
            type: String as PropType<string | null>,
            default: null
        },
        items: {
            type: Array as PropType<string[] | null>,
            default: null
        },
        withButton: {
            type: Boolean,
            default: false
        },
        labelButton: {
            type: String,
            default: ""
        },
        urlButton: {
            type: String,
            default: ""
        },
        isMini: {
            type: Boolean,
            default: false
        },
        type: {
            type: String as PropType<AlertType>,
            default: undefined
        },
        anonymized: {
            type: Boolean,
            default: false
        },
        withAlertRole: {
            type: Boolean,
            default: false
        },
        dataTestId: {
            type: String as PropType<string | null>,
            default: null
        },
        noBackground: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        alertContainerClasses(): string[] {
            if (this.isMini) {
                return ["alert-container", "alert-container-sm"];
            } else {
                return ["alert-container"];
            }
        },
        alertClass(): string | null {
            switch (this.type) {
                case "info":
                    return "alert-info";
                case "danger":
                    return "alert-danger";
                case "success":
                    return "alert-success";
                case "warning":
                    return "alert-warning";
                default:
                    return null;
            }
        },
        alertIconClass(): string {
            switch (this.type) {
                case "info":
                    return "icon-info";
                case "danger":
                    return "icon-error-severe";
                case "success":
                    return "icon-tick-circle";
                case "warning":
                    return "icon-warning-important";
                case "rewards":
                    return "icon-rewards";
                case "voucher":
                    return "icon-voucher-tag";
                default:
                    return "icon-info";
            }
        },
        buttonClass(): string {
            if (this.isMini) {
                return "ob1-link-small";
            } else {
                return "ob1-link";
            }
        }
    }
});
</script>

<style lang="scss" scoped>
.font-weight-normal {
    font-weight: normal;
}
</style>
